import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import ProfileMemberCard from "../common/ProfileMemberCard";

const TARGET_PROFILE_IDS = [
  "HS2208",
  "HS2077",
  "HS2210",
  "HS2088",
  "HS2209",
  "HS2206",
  "HS2185",
];

const RecentlyJoined = () => {
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const idsParam = TARGET_PROFILE_IDS.map((id) => String(id).replace(/^HS/i, "")).join(",");
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/get-handpicked-users.php`,
        { params: { ids: idsParam } }
      )
      .then((response) => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setUsers(response.data);
        } else {
          setUsers([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching handpicked matches:", error);
        setUsers([]);
      });
  }, []);

  // Only the fixed TARGET_PROFILE_IDS, in exact order, using real DB rows when present.
  const displayUsers = useMemo(() => {
    const normalizeId = (id) => {
      if (!id) return "";
      const str = String(id).trim().toUpperCase();
      return str.startsWith("HS") ? str : `HS${str}`;
    };

    const userMap = new Map();
    if (Array.isArray(users)) {
      users.forEach((u) => {
        if (!u) return;
        const uid = normalizeId(u.user_id || u.id || u.matrimony_id || u.uid);
        if (uid && !userMap.has(uid)) {
          userMap.set(uid, u);
        }
      });
    }

    return TARGET_PROFILE_IDS
      .filter((targetId) => userMap.has(targetId))
      .map((targetId) => ({ ...userMap.get(targetId) }));
  }, [users]);

  return (
    <section className="handpicked-matches-section">
      <div className="handpicked-inner-container">
        {/* Top Ornamental Flourish */}
        <div className="ornamental-header">
          <div className="ornament-top">
            <svg width="120" height="24" viewBox="0 0 120 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12C28 12 35 4 48 4C55 4 57 12 60 12C63 12 65 4 72 4C85 4 92 12 110 12" stroke="#d4a373" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="60" cy="12" r="4.5" fill="#e53d5c" />
              <circle cx="56" cy="12" r="2" fill="#d4a373" />
              <circle cx="64" cy="12" r="2" fill="#d4a373" />
              <circle cx="60" cy="8" r="2" fill="#d4a373" />
              <circle cx="60" cy="16" r="2" fill="#d4a373" />
              <circle cx="25" cy="10" r="1.5" fill="#d4a373" />
              <circle cx="95" cy="10" r="1.5" fill="#d4a373" />
            </svg>
          </div>

          <h2 className="handpicked-title">
            Handpicked Matches <span className="title-highlight">for You</span>
          </h2>
          <p className="handpicked-subtitle">
            Carefully selected profiles that match your preferences
          </p>

          <div className="ornament-bottom">
            <svg width="60" height="16" viewBox="0 0 60 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 8H22M38 8H60" stroke="#d4a373" strokeWidth="1" strokeLinecap="round" />
              <polygon points="30,2 35,8 30,14 25,8" fill="#d4a373" />
            </svg>
          </div>
        </div>

        {/* Carousel Slider with Custom Navigation */}
        <div className="handpicked-slider-wrapper">
          <button
            type="button"
            className="matches-nav-btn matches-nav-prev"
            aria-label="Previous profiles"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button
            type="button"
            className="matches-nav-btn matches-nav-next"
            aria-label="Next profiles"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            navigation={{
              prevEl: ".matches-nav-prev",
              nextEl: ".matches-nav-next",
            }}
            pagination={{
              clickable: true,
              el: ".matches-pagination",
            }}
            autoplay={{
              delay: 3500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop={displayUsers.length >= 5}
            spaceBetween={16}
            slidesPerView={1.15}
            breakpoints={{
              480: {
                slidesPerView: 2,
                spaceBetween: 12,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 14,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 16,
              },
              1200: {
                slidesPerView: 5,
                spaceBetween: 16,
              },
            }}
          >
            {displayUsers.map((member, index) => (
              <SwiperSlide key={member.user_id || index}>
                <ProfileMemberCard
                  member={member}
                  variant="luxury"
                  nestedSlider
                  hideName
                  onClick={() => navigate("/login")}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Pagination Dots Container */}
          <div className="matches-pagination" />
        </div>
      </div>

      <style>{`
        .handpicked-matches-section {
          position: relative;
          padding: 60px 0 55px;
          background: linear-gradient(180deg, #fdfbf9 0%, #fbf5f2 50%, #faf3f0 100%);
          overflow: hidden;
        }

        .handpicked-inner-container {
          max-width: 1440px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        .ornamental-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .ornament-top {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 6px;
        }

        .handpicked-title {
          font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
          font-size: 2.35rem;
          font-weight: 700;
          color: #1e242d;
          margin: 0 0 6px;
          letter-spacing: -0.01em;
          line-height: 1.25;
        }

        .handpicked-title .title-highlight {
          color: #e53d5c;
          font-family: inherit;
          font-weight: 700;
        }

        .handpicked-subtitle {
          font-family: 'Outfit', 'Plus Jakarta Sans', -apple-system, sans-serif;
          font-size: 1rem;
          color: #667085;
          margin: 0 0 8px;
          font-weight: 400;
        }

        .ornament-bottom {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 4px;
        }

        .handpicked-slider-wrapper {
          position: relative;
          padding: 4px 6px 10px;
        }

        /* Navigation Buttons */
        .matches-nav-btn {
          position: absolute;
          top: 45%;
          transform: translateY(-50%);
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: #ffffff;
          border: 1px solid rgba(229, 61, 92, 0.15);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
          color: #e53d5c;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          outline: none;
        }

        .matches-nav-prev {
          left: -18px;
        }

        .matches-nav-next {
          right: -18px;
        }

        .matches-nav-btn:hover {
          background: #e53d5c;
          color: #ffffff;
          box-shadow: 0 6px 20px rgba(229, 61, 92, 0.3);
          transform: translateY(-50%) scale(1.08);
        }

        .matches-nav-btn.swiper-button-disabled {
          opacity: 0.3;
          pointer-events: none;
        }

        /* Swiper Slide adjustments */
        .handpicked-slider-wrapper .swiper {
          padding: 8px 2px 16px;
          overflow: hidden;
        }

        .handpicked-slider-wrapper .swiper-slide {
          height: auto;
          display: flex;
        }

        .handpicked-slider-wrapper .member-card {
          height: 100%;
        }

        /* Custom Pagination */
        .matches-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 22px;
          position: relative;
          z-index: 5;
        }

        .matches-pagination .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #d8d0cd;
          opacity: 0.8;
          transition: all 0.3s ease;
          margin: 0 !important;
          cursor: pointer;
        }

        .matches-pagination .swiper-pagination-bullet-active {
          background: #e53d5c !important;
          width: 10px;
          height: 10px;
          opacity: 1;
          box-shadow: 0 2px 8px rgba(229, 61, 92, 0.4);
        }

        @media (max-width: 991px) {
          .handpicked-matches-section {
            padding: 45px 0 40px;
          }
          .handpicked-title {
            font-size: 1.95rem;
          }
          .matches-nav-prev {
            left: -8px;
          }
          .matches-nav-next {
            right: -8px;
          }
        }

        @media (max-width: 768px) {
          .handpicked-matches-section {
            padding: 38px 0 32px;
          }
          .handpicked-title {
            font-size: 1.65rem;
          }
          .handpicked-subtitle {
            font-size: 0.92rem;
          }
          .matches-nav-btn {
            display: none;
          }
          .handpicked-inner-container {
            padding: 0 16px;
          }
        }
      `}</style>
    </section>
  );
};

export default RecentlyJoined;
